import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Plus, Tag } from "lucide-react";

// Searchable category selector with inline create.
// value: category name (string) · categories: [{id,name,active,product_count}]
export function CategoryCombobox({ value, categories = [], onChange, onCreate, testId = "product-category" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const active = categories.filter((c) => c.active !== false);
  const q = query.trim().toLowerCase();
  const filtered = q ? active.filter((c) => c.name.toLowerCase().includes(q)) : active;
  const exists = active.some((c) => c.name.toLowerCase() === q);
  const canCreate = q.length > 0 && !exists && typeof onCreate === "function";

  const pick = (name) => { onChange(name); setOpen(false); setQuery(""); };
  const doCreate = async () => {
    const name = query.trim();
    if (!name) return;
    const ok = await onCreate(name);
    if (ok !== false) pick(name);
  };

  return (
    <Popover modal open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open}
          className="w-full justify-between font-normal" data-testid={testId}>
          <span className={value ? "truncate" : "text-muted-foreground"}>{value || "Pilih kategori"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-56" align="start" sideOffset={6}>
        <Command shouldFilter={false}>
          <CommandInput placeholder="Cari atau buat kategori..." value={query} onValueChange={setQuery} data-testid={`${testId}-input`} />
          <CommandList className="max-h-60 overflow-y-auto overscroll-contain">
            {filtered.length === 0 && !canCreate && !value && (
              <div className="py-6 text-center text-sm text-muted-foreground">Belum ada kategori</div>
            )}
            <CommandGroup>
              {value && (
                <CommandItem value="__clear__" onSelect={() => pick("")} data-testid={`${testId}-clear`}>
                  <Tag className="h-3.5 w-3.5 mr-2 opacity-50" />Tanpa kategori
                </CommandItem>
              )}
              {filtered.map((c) => (
                <CommandItem key={c.id} value={c.name} onSelect={() => pick(c.name)} data-testid="category-option">
                  <Check className={`h-4 w-4 mr-2 ${value === c.name ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{c.product_count ?? 0}</span>
                </CommandItem>
              ))}
              {canCreate && (
                <CommandItem value="__create__" onSelect={doCreate} data-testid={`${testId}-create`}>
                  <Plus className="h-3.5 w-3.5 mr-2" />Buat kategori "{query.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
